import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Save, ExternalLink, Copy, Palette, Type, Image } from "lucide-react";
import type { LandingPageConfig } from "@/types/applicant";

const LandingPageSettings = () => {
  const [config, setConfig] = useState<LandingPageConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase
        .from("landing_page_config")
        .select("*")
        .single();

      if (error) throw error;
      setConfig(data as LandingPageConfig);
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("landing_page_config")
        .update({
          title: config.title,
          subtitle: config.subtitle,
          description: config.description,
          background_color: config.background_color,
          text_color: config.text_color,
          accent_color: config.accent_color,
          logo_url: config.logo_url,
          background_image_url: config.background_image_url,
          show_company_info: config.show_company_info,
          custom_css: config.custom_css,
          is_active: config.is_active,
        })
        .eq("id", config.id);

      if (error) throw error;
      toast.success("Einstellungen gespeichert");
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const productionUrl = "https://perf-boost-app.lovable.app/apply";

  const copyLink = () => {
    navigator.clipboard.writeText(productionUrl);
    toast.success("Link kopiert");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Konfiguration konnte nicht geladen werden
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            Landing Page Link
          </CardTitle>
          <CardDescription>
            Diesen Link können Sie auf Ihrer Webseite einbinden
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input value={productionUrl} readOnly />
            <Button variant="outline" onClick={copyLink}>
              <Copy className="h-4 w-4 mr-2" />
              Kopieren
            </Button>
            <Button variant="outline" onClick={() => window.open(productionUrl, "_blank")}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Öffnen
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="h-5 w-5" />
            Texte
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input
              value={config.title}
              onChange={(e) => setConfig({ ...config, title: e.target.value })}
              placeholder="Karriere bei uns"
            />
          </div>
          <div className="space-y-2">
            <Label>Untertitel</Label>
            <Input
              value={config.subtitle || ""}
              onChange={(e) => setConfig({ ...config, subtitle: e.target.value })}
              placeholder="Werden Sie Teil unseres Teams"
            />
          </div>
          <div className="space-y-2">
            <Label>Beschreibung</Label>
            <Textarea
              value={config.description || ""}
              onChange={(e) => setConfig({ ...config, description: e.target.value })}
              placeholder="Wir suchen motivierte Mitarbeiter..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Farben
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Hintergrundfarbe</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={config.background_color}
                  onChange={(e) => setConfig({ ...config, background_color: e.target.value })}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={config.background_color}
                  onChange={(e) => setConfig({ ...config, background_color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Textfarbe</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={config.text_color}
                  onChange={(e) => setConfig({ ...config, text_color: e.target.value })}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={config.text_color}
                  onChange={(e) => setConfig({ ...config, text_color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Akzentfarbe (Button)</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={config.accent_color}
                  onChange={(e) => setConfig({ ...config, accent_color: e.target.value })}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={config.accent_color}
                  onChange={(e) => setConfig({ ...config, accent_color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="h-5 w-5" />
            Bilder
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Logo URL</Label>
            <Input
              value={config.logo_url || ""}
              onChange={(e) => setConfig({ ...config, logo_url: e.target.value })}
              placeholder="https://example.com/logo.png"
            />
          </div>
          <div className="space-y-2">
            <Label>Hintergrundbild URL</Label>
            <Input
              value={config.background_image_url || ""}
              onChange={(e) => setConfig({ ...config, background_image_url: e.target.value })}
              placeholder="https://example.com/background.jpg"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Weitere Einstellungen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Firmeninformationen anzeigen</Label>
              <p className="text-sm text-muted-foreground">
                Zeigt Firmenname und Standort aus den Einstellungen
              </p>
            </div>
            <Switch
              checked={config.show_company_info}
              onCheckedChange={(checked) =>
                setConfig({ ...config, show_company_info: checked })
              }
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Landing Page aktiv</Label>
              <p className="text-sm text-muted-foreground">
                Deaktivierte Seiten zeigen eine Fehlermeldung
              </p>
            </div>
            <Switch
              checked={config.is_active}
              onCheckedChange={(checked) =>
                setConfig({ ...config, is_active: checked })
              }
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Speichern...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Speichern
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default LandingPageSettings;
